import { AdvancedEditCut } from '@app/routes/content/routes/recordings/domain';
import { v4 as uuidv4 } from 'uuid';

import type { FlatWord } from './build-flat-words';

/**
 * Splits the cuts array so that the time range [startMs, endMs] is excluded
 * from every cut that overlaps it.
 *
 * This is the "restore words / segment" operation: the caller passes a range
 * that should be *removed from* the cut list (i.e. restored to the timeline).
 *
 * Algorithm for each affected cut:
 *
 *   cut:  |============cut============|
 *   range:         |==range==|
 *   result: |=left=|          |=right=|
 *
 * Edge cases:
 *   - Range covers start of cut  → only trailing piece remains.
 *   - Range covers end of cut    → only leading piece remains.
 *   - Range covers entire cut    → cut is removed.
 *   - Zero-duration results (start === end) are dropped.
 *
 * Non-overlapping cuts are passed through unchanged.
 */
export const splitCutRange = (
  cuts: AdvancedEditCut[],
  startMs: number,
  endMs: number,
  recordingId: string,
): AdvancedEditCut[] => {
  const result: AdvancedEditCut[] = [];

  for (const cut of cuts) {
    const overlaps = startMs < cut.end_time_ms && endMs > cut.start_time_ms;

    if (!overlaps) {
      // No overlap — keep untouched.
      result.push(cut);
      continue;
    }

    // Leading piece: cut.start_time_ms → startMs
    if (cut.start_time_ms < startMs) {
      result.push({
        id: uuidv4(),
        recording_id: recordingId,
        start_time_ms: cut.start_time_ms,
        end_time_ms: startMs,
        transition: cut.transition,
      });
    }

    // Trailing piece: endMs → cut.end_time_ms
    if (cut.end_time_ms > endMs) {
      result.push({
        id: uuidv4(),
        recording_id: recordingId,
        start_time_ms: endMs,
        end_time_ms: cut.end_time_ms,
        transition: cut.transition,
      });
    }
    // If neither piece exists the cut is fully consumed — nothing pushed.
  }

  return result;
};

/**
 * Given a flat index of a cut word, finds ALL flat indices whose words fall
 * in the same deleted gap (the gap between two consecutive cuts, before the
 * first cut, or after the last cut).
 *
 * The cuts array defines INCLUDED regions. A "gap" is a time range NOT
 * covered by any cut. Words that fall in the same gap as the clicked word
 * are returned as an array of flat indices.
 */
export const findGapWordIndices = (
  flatWords: FlatWord[],
  clickedFlatIdx: number,
  cuts: AdvancedEditCut[],
): number[] => {
  const clickedWord = flatWords[clickedFlatIdx];
  if (!clickedWord || !clickedWord.isCut) return [];

  const sorted = [...cuts].sort((a, b) => a.start_time_ms - b.start_time_ms);

  const clickedStartMs = clickedWord.word.start * 1000;
  const clickedEndMs = clickedWord.word.end * 1000;

  // Determine which gap the clicked word falls in.
  let gapStart: number;
  let gapEnd: number;

  if (sorted.length === 0) {
    // No cuts at all — the entire recording is one big gap
    gapStart = 0;
    gapEnd = Infinity;
  } else if (clickedEndMs <= sorted[0].start_time_ms) {
    // Before the first cut
    gapStart = 0;
    gapEnd = sorted[0].start_time_ms;
  } else if (clickedStartMs >= sorted[sorted.length - 1].end_time_ms) {
    // After the last cut
    gapStart = sorted[sorted.length - 1].end_time_ms;
    gapEnd = Infinity;
  } else {
    // Between two adjacent cuts
    gapStart = -1;
    gapEnd = -1;
    for (let i = 0; i < sorted.length - 1; i++) {
      const gStart = sorted[i].end_time_ms;
      const gEnd = sorted[i + 1].start_time_ms;
      if (clickedStartMs >= gStart && clickedEndMs <= gEnd) {
        gapStart = gStart;
        gapEnd = gEnd;
        break;
      }
    }
    if (gapStart === -1) return [clickedFlatIdx]; // Fallback
  }

  return flatWords.reduce<number[]>((acc, fw, idx) => {
    if (
      fw.isCut &&
      fw.word.start * 1000 >= gapStart &&
      fw.word.end * 1000 <= gapEnd
    ) {
      acc.push(idx);
    }
    return acc;
  }, []);
};

/**
 * Restores the entire deleted gap containing the clicked word by merging or
 * extending the adjacent cuts so the gap is no longer present.
 *
 * Cases:
 *   - Before first cut  → set cuts[0].start_time_ms = 0
 *   - Between cuts i and i+1 → merge into one cut spanning both
 *   - After last cut    → set cuts[last].end_time_ms = recordingDurationMs
 *
 * Returns a new cuts array (sorted by start_time_ms) with the gap closed.
 */
export const restoreGap = (
  cuts: AdvancedEditCut[],
  clickedWordStartMs: number,
  clickedWordEndMs: number,
  recordingDurationMs: number,
): AdvancedEditCut[] => {
  const sorted = [...cuts].sort((a, b) => a.start_time_ms - b.start_time_ms);

  if (sorted.length === 0) {
    // No cuts — nothing to restore (entire timeline already included)
    return cuts;
  }

  // Before the first cut
  if (clickedWordEndMs <= sorted[0].start_time_ms) {
    const newCuts = sorted.map((c, i) =>
      i === 0 ? { ...c, start_time_ms: 0 } : c,
    );
    return newCuts;
  }

  // After the last cut
  if (clickedWordStartMs >= sorted[sorted.length - 1].end_time_ms) {
    const newCuts = sorted.map((c, i) =>
      i === sorted.length - 1 ? { ...c, end_time_ms: recordingDurationMs } : c,
    );
    return newCuts;
  }

  // Between two adjacent cuts — find the pair and merge them
  for (let i = 0; i < sorted.length - 1; i++) {
    const gStart = sorted[i].end_time_ms;
    const gEnd = sorted[i + 1].start_time_ms;
    if (clickedWordStartMs >= gStart && clickedWordEndMs <= gEnd) {
      // Merge cut i and cut i+1 into one
      const merged: AdvancedEditCut = {
        ...sorted[i],
        end_time_ms: sorted[i + 1].end_time_ms,
      };
      const newCuts = [...sorted.slice(0, i), merged, ...sorted.slice(i + 2)];
      return newCuts;
    }
  }

  // Fallback — no matching gap found, return unchanged
  return cuts;
};
